import { useEffect, useRef, useState } from "react";

import { AsyncTaskStatusUpdateEvent, AsyncTaskStatusUpdateEventType } from "@performyard/service-contracts";

import { streamCompanyAsyncTaskStatus, streamUserAsyncTask } from "../../api/services/defs/internal-api/async-tasks";

type UseAsyncTaskStreamReturn = {
	task: AsyncTaskStatusUpdateEventType | null;
	isConnected: boolean;
	error: Error | null;
	isRetrying: boolean;
	// Monotonic for a given taskId: true once the stream connects, never reset until taskId changes.
	// Unlike observing the transient `isConnected`, this survives React batching a fast connect→close.
	hasConnected: boolean;
};

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff in milliseconds

/**
 * Hook to stream async task status updates via Server-Sent Events (SSE).
 * Automatically connects on mount, handles retries with exponential backoff,
 * and provides type-safe task updates.
 *
 * @param taskId - The ID of the async task to monitor (null or empty string to skip connection)
 * @param options.boundRetriesOnStalledProgress - when true the retry budget resets only on forward
 *   progress, so a stream that only re-sends the same snapshot before dropping can exhaust (used by
 *   feedback's terminal-or-die watcher). Default false resets on every connect, so long-running
 *   consumers (e.g. review-cycle builds) don't surface a false failure on transient drops.
 * @returns Object containing task data, connection status, error state, and retry status
 *
 * @example
 * const { task, isConnected, error, isRetrying } = useAsyncTaskStream(taskId);
 *
 * if (error) {
 *   return <div>Error: {error.message}</div>;
 * }
 *
 * if (!task) {
 *   return <div>Loading...</div>;
 * }
 *
 * const progress = task.progressTotal > 0 ? (task.progressCount / task.progressTotal) * 100 : 0;
 *
 * return (
 *   <div>
 *     <p>Status: {task.status}</p>
 *     <p>Progress: {progress}%</p>
 *   </div>
 * );
 */
export const useAsyncTaskStream = (
	taskId: string | null,
	{
		boundRetriesOnStalledProgress = false,
		scope = "company",
	}: {
		boundRetriesOnStalledProgress?: boolean;
		// "company" (default) streams any company task but only non-sensitive fields —
		// for shared surfaces (review-cycle build, insights, embeddings) watching tasks
		// they don't own. "user" streams the caller's own task with full data (incl.
		// signed URLs); it is owner-only on the server, so only pass it for own tasks.
		scope?: "user" | "company";
	} = {}
): UseAsyncTaskStreamReturn => {
	const [task, setTask] = useState<AsyncTaskStatusUpdateEventType | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [isRetrying, setIsRetrying] = useState(false);
	const [hasConnected, setHasConnected] = useState(false);

	const abortControllerRef = useRef<AbortController | null>(null);
	const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryCountRef = useRef(0);
	const lastProgressSigRef = useRef<string | null>(null);
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		// Reset per taskId so a remount/new task starts fresh; set true once on connect below.
		setHasConnected(false);
		lastProgressSigRef.current = null;

		if (!taskId) {
			setTask(null);
			setError(null);
			setIsRetrying(false);
			retryCountRef.current = 0;
			return;
		}

		const connectToStream = async (): Promise<void> => {
			if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
				setError(new Error("Max retry attempts reached"));
				setIsRetrying(false);
				return;
			}

			// Create new abort controller for this connection attempt
			abortControllerRef.current = new AbortController();

			try {
				// "company" watches a task the caller may not own, so it streams the
				// status-only endpoint (non-sensitive fields); "user" gets the full stream.
				const reader =
					scope === "company"
						? await streamCompanyAsyncTaskStatus(taskId, abortControllerRef.current.signal)
						: await streamUserAsyncTask(taskId, abortControllerRef.current.signal);

				setIsConnected(true);
				setHasConnected(true);
				setIsRetrying(false);
				setError(null);
				// Default consumers clear the budget on each connect; bounded mode skips so a stream that only re-sends the same snapshot before dropping can still exhaust.
				if (!boundRetriesOnStalledProgress) {
					retryCountRef.current = 0;
				}

				// Read the stream
				const decoder = new TextDecoder();
				let buffer = "";

				while (isMountedRef.current) {
					const { done, value } = await reader.read();

					if (done) {
						break;
					}

					// Decode the chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });

					// Process complete SSE messages (separated by double newlines)
					const lines = buffer.split("\n\n");
					buffer = lines.pop() || ""; // Keep incomplete message in buffer

					for (const line of lines) {
						if (line.startsWith("data:")) {
							const data = line.substring(5).trim(); // Remove "data:" prefix

							try {
								const parsed = JSON.parse(data);
								const validated = AsyncTaskStatusUpdateEvent.parse(parsed);
								setTask(validated);
								// Bounded mode resets only on forward progress — the route re-sends the same snapshot each reconnect, so resetting on it would dodge exhaustion forever.
								if (boundRetriesOnStalledProgress) {
									const progressSig = `${validated.status}:${validated.progressCount}`;
									if (progressSig !== lastProgressSigRef.current) {
										lastProgressSigRef.current = progressSig;
										retryCountRef.current = 0;
									}
								}

								if (validated.status === "completed" || validated.status === "failed") {
									reader.cancel();
									setIsConnected(false);
									return;
								}
							} catch (parseError) {
								// Continue processing other messages
							}
						}
					}
				}

				setIsConnected(false);
			} catch (err) {
				// Don't treat abort as an error
				if (err instanceof Error && err.name === "AbortError") {
					return;
				}

				// Only update state if component is still mounted
				if (!isMountedRef.current) return;

				const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
				setError(new Error(errorMessage));
				setIsConnected(false);

				// Schedule retry with exponential backoff
				const retryDelay = RETRY_DELAYS[Math.min(retryCountRef.current, RETRY_DELAYS.length - 1)];
				retryCountRef.current += 1;
				setIsRetrying(true);

				retryTimeoutRef.current = setTimeout(() => {
					if (isMountedRef.current) {
						connectToStream();
					}
				}, retryDelay);
			}
		};

		// Start the connection
		connectToStream();

		// Cleanup function
		return () => {
			isMountedRef.current = false;

			// Cancel any in-flight request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Clear any pending retry
			if (retryTimeoutRef.current) {
				clearTimeout(retryTimeoutRef.current);
			}

			setIsConnected(false);
		};
	}, [taskId, boundRetriesOnStalledProgress, scope]);

	return {
		task,
		isConnected,
		error,
		isRetrying,
		hasConnected,
	};
};
